import { Injectable } from '@angular/core';
import { Effect, Actions, toPayload } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { Action, Store } from '@ngrx/store';

import { StageService } from '../services/stage.service';
import { IApplicationState } from '../../stateManagement/states/applicationState';

import * as StagesActions from '../../stateManagement/actions/data/stages';
import * as StageActions from '../../stateManagement/actions/data/stage';
import * as fromRoot from '../../stateManagement/rootReducer';
import * as stageTypes from '../types';
import * as commonTypes from '../../shared/types';

import * as _ from 'lodash';
import * as helpers from '../../shared/helpers';

import { environment } from '../../../environments/environment';
import { IResponse } from '../../shared/types/response';
import * as commonConstants from '../../shared/types/constants';

import { BlockUIService } from 'ng-block-ui';
import { WindowRefService } from '../../shared/services/window-ref.service';
import {showWarningOnce} from 'tslint/lib/error';
import { PopNotificationService } from '../../shared/services/pop-notification.service';

@Injectable()
export class StageEffects {
  private blockUI_target = 'stage-section';
  private nativeWindow: any;
  private pageLength: number;
  private pages: stageTypes.IPage[];
  
  constructor(
    private notificationSvc: PopNotificationService,
    private actions$: Actions,
    private stageService: StageService,
    private store: Store<IApplicationState>,
    private blockUI: BlockUIService,
    private windowRef: WindowRefService) {
    this.nativeWindow = windowRef.getNativeWindow();
    this.store.select(fromRoot.getStageDataGetStagePagesLength).subscribe(pageLength => this.pageLength = pageLength);
    this.store.select(fromRoot.getStageDataGetSelectedPageData).subscribe((page: stageTypes.IPage) => {
      if (page) {
        this.store.dispatch(new StageActions.SelectPageSuccessAction({
          id: String(page.id),
          name: page.name
        }));
      }
    });
  }

  // tslint:disable-next-line:member-ordering
  @Effect()
  get_stage_Info$: Observable<Action> = this.actions$
    .ofType(StagesActions.ActionTypes.GET_STAGE_INFO)
    .switchMap( (action: Action) => {
      return this.stageService.getStages()
        .map((stageInfo: stageTypes.IStarterResponse) => {
          return new StagesActions.GetStageInfoSuccessAction(stageInfo);
        })
        .catch(error => {
            this.stageService.handleError(error.status,
              'Stage Information Retrieve Failure', this.extractErrorMessage(error.json()),
              new StagesActions.GetStageInfoAction({}));
            return Observable.of(new StagesActions.GetStageInfoFailAction({ message: error.json() }));
        });
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  get_stage_info_success$: Observable<Action> = this.actions$
    .ofType(StagesActions.ActionTypes.GET_STAGE_INFO_SUCCESS)
    .map(toPayload)
    .filter((payload: stageTypes.IStarterResponse) => String(payload._embedded.stages[0].id) !== '')
    .map ((payload: stageTypes.IStarterResponse) => {
        return new StageActions.GetStageAction(payload._embedded.stages[0].id);
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  get_stage_definition$: Observable<Action> = this.actions$
    .ofType(StageActions.ActionTypes.GET_STAGE)
    .map(toPayload)
    .switchMap( (stageId: number) => {
      return this.stageService.getStageDefinition(stageId)
        .map((stageData: stageTypes.IStageResponse) => new StageActions.GetStageSuccessAction(stageData))
        .catch(error => {
            this.stageService.handleError(error.status,
              'Stage Data Retrieve Failure', this.extractErrorMessage(error.json()),
              new StageActions.GetStageAction(stageId));
            return Observable.of(new StageActions.GetStageFailAction({ message: error.json() }));
        });
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  download_attachment$: Observable<Action> = this.actions$
    .ofType(StageActions.ActionTypes.DOWNLOAD_ATTACHMENT)
    .map(toPayload)
    .mergeMap( (attachment: stageTypes.IAttachmentDownload) => {
      return this.stageService.downloadAttachment(attachment.link, attachment.authRequired || false)
        .mergeMap( (data) => {
          const payload = Object.assign({}, attachment, {
              result: (attachment.authRequired) ? data : (<any>data)._body
            });
          return [
            new StageActions.DownloadAttachmentSuccessAction(payload),
            new StageActions.SaveAttachmentAction(payload)
          ];
        })
        .catch(error => {
            if (_.indexOf(commonConstants.ERROR_STATUS, error.status) != -1) {
              this.stageService.handleError(error.status,
                'Attachment Download Failure', this.extractErrorMessage(error.json()),
                new StageActions.DownloadAttachmentAction(attachment));
            }
            return Observable.of(new StageActions.DownloadAttachmentFailAction({ message: error.json(), data: attachment }));
        });
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  save_attachment$: Observable<Action> = this.actions$
    .ofType(StageActions.ActionTypes.SAVE_ATTACHMENT)
    .map(toPayload)
    .map( (attachment: stageTypes.IAttachmentDownload) => {
      const isIOS = this.nativeWindow.Modernizr.appleios;
      if (isIOS ) {
        // currently do nothing
      } else {
        this.stageService.saveAttachment(attachment.result, (attachment.name === '') ? attachment.action.replace(/\s+/g, '_') : attachment.name);
      }
      return new StageActions.SaveAttachmentSuccessAction(attachment);
    } );

  // tslint:disable-next-line:member-ordering
  @Effect()
  document_confirmation$: Observable<Action> = this.actions$
    .ofType(StageActions.ActionTypes.DOCUMENT_CONFIRMATION)
    .map(toPayload)
    .mergeMap( (attachment: stageTypes.IAttachmentDownload) => {
      return this.stageService.documentConfirmation(attachment.questionId)
        .map( (res) => {
          return new StageActions.DocumentConfirmationSuccessAction(attachment);
        })
        .catch(error => {
          if (_.indexOf(commonConstants.ERROR_STATUS, error.status) != -1) {
            this.stageService.handleError(error.status,
              'Document confirmation Failure', this.extractErrorMessage(error.json()),
              new StageActions.DocumentConfirmationAction(attachment));
          }
            return Observable.of(new StageActions.DocumentConfirmationFailAction({ message: error.json(), data: attachment }));
        });
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  update_stage$: Observable<Action> = this.actions$
    .ofType(StageActions.ActionTypes.UPDATE_STAGE  )
    .map(toPayload)
    .switchMap( (attachment: stageTypes.IAttachmentDownload) => {
      return this.stageService.serverAction(attachment)
        .map( (data) => {
          return new StageActions.UpdateStageSuccessAction( _.has(data, 'page') ? Number(data.page) : 1);
        })
        .catch(error => {
          //console.log(error);
            this.stageService.handleError(error.status,
              'Update to Previous Stage Failure', this.extractErrorMessage(error.json()),
              new StageActions.UpdateStageAction(attachment));
            return Observable.of(new StageActions.UpdateStageFailAction({ message: error.json(), data: attachment }));
        });
    });
  
  // tslint:disable-next-line:member-ordering
  @Effect()
  update_stage_success$: Observable<Action> = this.actions$
    .ofType(StageActions.ActionTypes.UPDATE_STAGE_SUCCESS)
    .map(toPayload)
    .mergeMap( (pageIndex: number) => {
      return [
        new StageActions.ResetStageAction(pageIndex)
      ];
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  upload_form_data$: Observable<Action> = this.actions$
    .ofType(StageActions.ActionTypes.SAVE_FORM_DATA)
    .map(toPayload)
    .switchMap( (payload: stageTypes.IFormData) => {
      //Siddharth ADO-136687, added the if-condition. if block-ui is not running then run it.
      //The block-ui is started in the personal-detail components. if not started, it will be started here
      if (!this.blockUI.isRunning) {
        this.blockUI.start(this.blockUI_target, 'Saving data.....');
      }
      return this.stageService.saveFormData(payload.currentPageId, payload.pageData)
        .map ( (response) => {
          this.blockUI.stop(this.blockUI_target);
          return new StageActions.SaveFormDataSuccessAction(payload);
        })
        .catch(error => {
           // this.blockUI.start(this.blockUI_target, 'Error saving data.....');
          this.blockUI.stop(this.blockUI_target);
          if (_.indexOf(commonConstants.ERROR_STATUS, error.status) != -1) {
            this.stageService.handleError(error.status,
              'Save form data Failure ', this.extractErrorMessage(error.json()),
              new StageActions.SaveFormDataAction(payload));
          }
            this.notificationSvc.popNotification({
                type: 'error',
                title: 'Form still contains errors.',
                body: JSON.stringify(error.json().message)
            });//(error);
          //  alert("Got the error on saving"+ JSON.stringify(error.json()));
          return Observable.of(new StageActions.SaveFormDataFailAction({ message: error.json(), data: payload }));
        });
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  change_page$: Observable<Action> = this.actions$
    .ofType(StageActions.ActionTypes.SAVE_FORM_DATA_SUCCESS)
    .map(toPayload)
    .map( (payload: stageTypes.IFormData) => {
      // only call accept stage at the last page
        if ((payload.currentPageId === payload.nextPageId) && 
          ( payload.nextPageId === this.pageLength) && 
      ( !_.has(payload.pageData, 'draft'))) {
            return new StageActions.AcceptStageAction({});
        } else {
          return new StageActions.SelectPageAction(Number(payload.nextPageId));
        }
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  accept_current_stage$: Observable<Action> = this.actions$
    .ofType(StageActions.ActionTypes.ACCEPT_STAGE)
    .map(toPayload)
    .switchMap( (payload) => {
      this.blockUI.start(this.blockUI_target, '');
      return this.stageService.acceptStage()
        .map ( (response) => {
          return new StageActions.AcceptStageSuccessAction(payload);
        })
        .catch(error => {
          this.blockUI.stop(this.blockUI_target);
          if (_.indexOf(commonConstants.ERROR_STATUS, error.status) != -1) {
            this.stageService.handleError(error.status,
              'Accept stage Failure', this.extractErrorMessage(error.json()),
              new StageActions.AcceptStageAction(payload));
          }
          return Observable.of(new StageActions.AcceptStageFailAction({ message: error.json(), data: payload }));
        });
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  accept_current_stage_success$: Observable<Action> = this.actions$
    .ofType(StageActions.ActionTypes.ACCEPT_STAGE_SUCCESS)
    .map(toPayload)
    .map( (payload) => {
      this.blockUI.stop(this.blockUI_target);
      return new StageActions.ResetStageAction(1);
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  server_action$: Observable<Action> = this.actions$
    .ofType(StageActions.ActionTypes.SERVER_ACTION)
    .map(toPayload)
    .mergeMap( (attachment: stageTypes.IAttachmentDownload) => {
      return this.stageService.serverAction(attachment)
        .map( (res) => {
          return new StageActions.ServerCallSuccessAction(attachment);
        })
        .catch(error => {
          //console.log(error);
          if (_.indexOf(commonConstants.ERROR_STATUS, error.status) != -1) {
            this.stageService.handleError(error.status,
              'Server Action Failure', this.extractErrorMessage(error.json()),
              new StageActions.ServerCallAction(attachment));
          }
            return Observable.of(new StageActions.ServerCallFailAction({ message: error.json(), data: attachment }));
        });
    });
  
  private extractErrorMessage(error): string {
    if (_.has(error, 'message')) {
      return error.message;
    }

    return JSON.stringify(error);
  }
}



// WEBPACK FOOTER //
// ./src/app/stage/effects/stage.effect.ts